import React from 'react';

import { Nav, List, ListItem, Link } from './SubNavigation.style';

import { EntityContext } from 'context/entityContext';
import { AllTopicTypes, ParentTopicTypes } from 'topics';
import getPathPrefix from 'helpers/topicNavigation/getPathPrefix';
import useMedia from 'hooks/useMedia';

import Layout from 'components/styled/Layout/Layout';
import WithNewLabel from 'components/styled/WithNewLabel/WithNewLabel';
import { ILabelValuePair } from 'types/shared';

interface ISubNavigationProps {
  topic: ParentTopicTypes;
  variants: ILabelValuePair<AllTopicTypes>[];
}

/**
 * Sub navigation is wrapped in a route and conditionally rendered above the entity content.
 * When the route matches, the variants of the topic are passed as a prop (such as `['dsp', 'official']`).
 *
 * @param {String} props.pathPrefix The base url
 * @param {String} props.topic currently active topic (such as 'charts')
 * @param {Array} props.variants the variants of the topic (such as 'dsp' for 'charts')
 */
const SubNavigation: React.FC<ISubNavigationProps> = (props) => {
  const { topic, variants } = props;

  const {
    entityType,
    entityIds,
    parentEntityId,
    compareMode,
    countryIds,
  } = React.useContext(EntityContext);

  const pathPrefix = getPathPrefix(compareMode, countryIds);
  const entities = entityIds.join(',');
  const isLargeScreen = useMedia('(min-width: 50em)');
  let topics = variants;
  if (entityType === 'project' || entityType === 'artist' || parentEntityId) {
    // parentEntityId means - Track or Album version
    topics = topics.filter(
      (topic: ILabelValuePair<string>) => topic.value !== 'certifications'
    );
  }
  if (entityType !== 'track') {
    topics = topics.filter(
      (topic: ILabelValuePair<string>) => topic.value !== 'positionTracker'
    );
  }
  if (entityType !== 'artist') {
    topics = topics.filter(
      (topic: ILabelValuePair<string>) => topic.value !== 'linkfire'
    );
  }
  if (
    topic === 'overview' &&
    (entityType === 'project' || entityType === 'artist')
  ) {
    topics = topics.filter((topic) => topic.value !== 'certifications');
  }

  const Navigation: React.FC = () => {
    return (
      <Nav aria-label="Secondary navigation">
        <List>
          {topics.map((variant) => (
            <ListItem key={variant.value}>
              <Link
                // ID for Google Tag Manager trigger
                id={`TopicNavigation-${variant.value}`}
                to={
                  parentEntityId && !compareMode
                    ? `${pathPrefix}/${entityType}/${parentEntityId}/version/${entities}/${topic}/${variant.value}`
                    : `${pathPrefix}/${entityType}/${entities}/${topic}/${variant.value}`
                }
              >
                <WithNewLabel isNew={false}>{variant.label}</WithNewLabel>
              </Link>
            </ListItem>
          ))}
        </List>
      </Nav>
    );
  };

  if (isLargeScreen) {
    return (
      <Layout.MaxWidth isNewDesign isWide>
        <Navigation />
      </Layout.MaxWidth>
    );
  }

  return <Navigation />;
};

export default SubNavigation;
